import { Guid } from "client_legacy/ndaw/guid";
import {
	ReactElement,
	useEffect,
	useMemo,
	useState
} from "react";
import { InterestedMagisterParty, MagisterLiveInformationProvider } from "./provider";
import { ScheduleKind } from "./Schedule";
import { ResultStatus, ScheduleResponseModel } from "client/Api/Workspace/LiveInformation/Magister/magisterLiveInformation";
import styles from "./DaySchedule.module.less";
import {
	BusyIndicator,
	DateSlider,
	SliderWithToggle
} from "client/Components";
import { TileRefresh } from "../TileRefresh/TileRefresh";
import { LiveTileContent } from "../LiveTileContent";
import { localize } from "client_legacy/ndaw/culture";
import { AppointmentListItem } from "./AppointmentListItem";
import { TimeZone } from "client/DateAndTime/timezones";
import { getStartOfDay } from "client/DateAndTime/dateTimeUtils";

export type DayScheduleProps = {
	tabId: Guid;
	connectionId: number;
	provider: MagisterLiveInformationProvider;
	date: Date;
	onDateChange(date: Date): void;
	isScheduleSelectionAvailable: boolean;
	onChangeScheduleKind(kind: ScheduleKind): void;
	isTodayButtonAvailable?: boolean;
	onNavigate(): void;
	timezone: TimeZone;
};

export const DaySchedule = (props: DayScheduleProps): ReactElement => {
	const {
		tabId,
		connectionId,
		provider,
		date,
		onDateChange,
		isScheduleSelectionAvailable,
		onChangeScheduleKind,
		isTodayButtonAvailable,
		onNavigate,
		timezone
	} = props;

	const [loadingState, setLoadingState] = useState<LoadingState>({
		isLoading: true
	});

	const startOfDate = useMemo(
		(): Date => getStartOfDay(date, timezone),
		[date, timezone]
	);

	useEffect(
		(): (() => void) => {
			const party: InterestedMagisterParty = {
				partyId: tabId,
				resourceId: {
					connectionId,
					date: startOfDate
				},
				informAboutGlobalRetrievalFailure: (): void => {
					setLoadingState({
						isLoading: false,
						hasGlobalFailure: true
					});
				},
				informAboutConsecutiveErrors: (): void => {
					setLoadingState({
						isLoading: false,
						hasTooManyConsecutiveErrors: true
					});
				},
				updateDaySchedule: (response: ScheduleResponseModel, isTeacher: boolean | null): void => {
					setLoadingState((x) => ({
						...x,
						isLoading: false,
						hasTooManyConsecutiveErrors: false,
						hasGlobalFailure: false,
						isTeacher,
						response
					}));
				}
			};

			setLoadingState((x) => ({
				...x,
				isLoading: true
			}));

			provider.register(party, { daySchedule: true });

			return () => {
				provider.unregister(party);
			};
		},
		[
			connectionId,
			provider,
			tabId,
			startOfDate
		]
	);

	const tileRefreshHandler = (): void => {
		setLoadingState((x) => ({
			...x,
			isLoading: true
		}));
		provider.forceReload();
	};

	const renderContent = (): ReactElement => {
		if (loadingState.isLoading) {
			return <BusyIndicator />;
		}

		if (loadingState.hasTooManyConsecutiveErrors) {
			return <TileRefresh onRefresh={tileRefreshHandler} />;
		}

		if (loadingState.hasGlobalFailure
			|| loadingState.response.status !== ResultStatus.Success
		) {
			return (
				<LiveTileContent.NoResults
					icon="gear"
					title={localize("_UnableToRetrieveItems")}
					message={localize("_WeWereUnableToRetrieveTheItems")}
					onRetry={tileRefreshHandler}
					trackingId="has-error"
				/>
			);
		}

		const { response } = loadingState;

		if (response.items.length === 0) {
			return (
				<LiveTileContent.NoResults
					icon="calendar"
					title={localize("_NoClassesThisDay")}
					message={localize("_YouDontHaveAnyClassesThisDay")}
					trackingId="no-items"
				/>
			);
		}

		return (
			<LiveTileContent.Scrollable>
				<LiveTileContent.List>
					{response.items.map((x) => (
						<AppointmentListItem
							key={x.id}
							appointment={x}
							onNavigate={onNavigate}
							timezone={timezone}
						/>
					))}
				</LiveTileContent.List>
			</LiveTileContent.Scrollable>
		);
	};

	return (
		<>
			<div className={styles.sliderContainer}>
				<SliderWithToggle>
					<SliderWithToggle.Slider>
						<DateSlider
							value={startOfDate}
							onChange={onDateChange}
							isTodayButtonAvailable={isTodayButtonAvailable}
							timezone={timezone}
						/>
					</SliderWithToggle.Slider>
					{isScheduleSelectionAvailable && (
						<SliderWithToggle.Toggle
							icon="calendar-week"
							onClick={() => onChangeScheduleKind(ScheduleKind.Week)}
						/>
					)}
				</SliderWithToggle>
			</div>
			{renderContent()}
		</>
	);
};
DaySchedule.displayName = "DaySchedule";

type LoadingState = {
	isLoading: true;
} | {
	isLoading: false;
} & (
		{
			hasTooManyConsecutiveErrors: true;
			hasGlobalFailure?: undefined;
		}
		|
		{
			hasGlobalFailure: true;
			hasTooManyConsecutiveErrors?: undefined;
		}
		|
		{
			hasTooManyConsecutiveErrors: false;
			hasGlobalFailure: false;
			isTeacher: boolean | null;
			response: ScheduleResponseModel;
		}
	);
